import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import StandardNavbar from '../components/StandardNavbar';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp, Mail, Phone, Clock, MapPin } from 'lucide-react';

function FAQ() {
  // ✅ DOS ESTADOS SEPARADOS para manejar acordeones independientes
  const [openCategory, setOpenCategory] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFAQ = async () => {
      try {
        setLoading(true);
        console.log('🔍 Cargando FAQ desde CMS...');
        
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/paginas-cms/preguntas-frecuentes`,
          {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Datos FAQ recibidos:', data);
          
          setPageData(data);
          
          // Procesar secciones del CMS (cada sección es una categoría)
          if (data.secciones && Array.isArray(data.secciones) && data.secciones.length > 0) {
            const categorias = data.secciones.map(seccion => ({
              id: seccion.id,
              category: seccion.titulo,
              questions: extraerPreguntasDeHTML(seccion.contenido)
            }));
            
            setFaqData(categorias);
            
            // Abrir la primera categoría por defecto
            //setOpenCategory(categorias[0].id);
          } else {
            console.warn('⚠️ No se encontraron secciones en el CMS');
            setFaqData(getFallbackData());
          }
        } else {
          console.error('❌ Error al cargar FAQ:', response.status);
          setError('No se pudo cargar las preguntas frecuentes');
          setFaqData(getFallbackData());
        }
      } catch (err) {
        console.error('❌ Error en fetch FAQ:', err);
        setError('Error al conectar con el servidor');
        setFaqData(getFallbackData());
      } finally {
        setLoading(false);
      }
    };

    fetchFAQ();
  }, []);

  /**
   * Extrae preguntas y respuestas desde el HTML estructurado del CMS
   */
  const extraerPreguntasDeHTML = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const questions = [];
    
    // Buscar todos los elementos con clase faq-item
    const faqItems = doc.querySelectorAll('.faq-item');
    
    if (faqItems.length > 0) {
      // Formato estructurado con clases
      faqItems.forEach((item, index) => {
        const questionEl = item.querySelector('.faq-question, h4');
        const answerEl = item.querySelector('.faq-answer, .faq-answer div');
        
        if (questionEl && answerEl) {
          questions.push({
            id: `q-${index}`,
            question: questionEl.textContent.trim(),
            answer: answerEl.innerHTML.trim()
          });
        }
      });
    } else {
      // Fallback: buscar por estructura h4 seguido de contenido
      const headers = doc.querySelectorAll('h4');
      
      headers.forEach((h4, index) => {
        const question = h4.textContent.trim();
        let answer = '';
        let nextEl = h4.nextElementSibling;
        
        // Recolectar contenido hasta el siguiente h4
        while (nextEl && nextEl.tagName !== 'H4') {
          answer += nextEl.outerHTML || '';
          nextEl = nextEl.nextElementSibling;
        }
        
        if (question && answer) {
          questions.push({
            id: `q-${index}`,
            question,
            answer: answer.trim()
          });
        }
      });
    }
    
    return questions;
  };

  /**
   * Datos de fallback en caso de error
   */
  const getFallbackData = () => [
    {
      id: 1,
      category: "Preguntas Generales",
      questions: [
        {
          id: 'q-1',
          question: "¿Cómo puedo contactarlos?",
          answer: `
            <p>Puedes contactarnos por diferentes medios:</p>
            <ul>
              <li><strong>Email:</strong> consultas@nocheydiatursimo.com</li>
              <li><strong>Teléfono:</strong> (011) 4432 8675</li>
              <li><strong>Oficina:</strong> Yerbal 1136 PB (C1406BWA), CABA, Argentina</li>
            </ul>
          `
        }
      ]
    }
  ];

  // ✅ TOGGLE PARA CATEGORÍAS
  const toggleCategory = (categoryId) => {
    if (openCategory === categoryId) {
      setOpenCategory(null);
      setOpenQuestion(null); // Cerrar preguntas al cerrar categoría
    } else {
      setOpenCategory(categoryId);
      setOpenQuestion(null); // Reset preguntas al cambiar de categoría
    }
  };

  // ✅ TOGGLE PARA PREGUNTAS
  const toggleQuestion = (questionId) => {
    setOpenQuestion(openQuestion === questionId ? null : questionId);
  };

  if (loading) {
    return (
      <>
        <StandardNavbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2AA8B0] mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando preguntas frecuentes...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageData?.titulo || 'Preguntas Frecuentes'} | Noche y Día Turismo</title>
        <meta 
          name="description" 
          content={pageData?.descripcion || "Encuentra respuestas a las preguntas más comunes sobre nuestros servicios de turismo"} 
        />
      </Helmet>
      
      <StandardNavbar />
      
      <div className="relative pt-16 min-h-screen bg-gray-50">
        {/* Header con gradiente */}
        <section className="bg-gradient-to-r from-[#2AA8B0] to-[#1E3A5F] text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold font-montserrat mb-4">
              {pageData?.titulo || '¿Cómo podemos ayudarte?'}
            </h1>
            <p className="text-lg md:text-xl opacity-90 max-w-2xl mx-auto">
              {pageData?.descripcion || 'Las siguientes preguntas son el resultado de años respondiendo a las necesidades de nuestros clientes!'}
            </p>
            {pageData?.fecha_actualizacion && (
              <p className="text-blue-200 text-sm mt-6">
                Última actualización: {new Date(pageData.fecha_actualizacion).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}
          </div>
        </section>

        {/* Contenido principal con categorías */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            {error && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-yellow-700">{error}</p>
              </div>
            )}

            {faqData.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <p className="text-gray-600">No hay preguntas frecuentes disponibles en este momento.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {faqData.map((category) => (
                  <div key={category.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                    {/* Cabecera de categoría (siempre visible) */}
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="w-full px-6 py-5 flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all duration-200 border-b-2 border-[#2AA8B0]"
                    >
                      <h2 className="text-xl md:text-2xl font-bold text-[#1E3A5F] font-montserrat flex items-center gap-3">
                        <span className="w-10 h-10 rounded-full bg-[#2AA8B0] text-white flex items-center justify-center text-lg">
                          {faqData.indexOf(category) + 1}
                        </span>
                        {category.category}
                      </h2>
                      {openCategory === category.id ? (
                        <ChevronUp className="w-6 h-6 text-[#2AA8B0] flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-[#2AA8B0] flex-shrink-0" />
                      )}
                    </button>

                    {/* Preguntas de la categoría (desplegables) */}
                    {openCategory === category.id && (
                      <div className="divide-y divide-gray-200">
                        {category.questions.map((faq) => {
                          const questionId = `${category.id}-${faq.id}`;
                          const isQuestionOpen = openQuestion === questionId;

                          return (
                            <div key={faq.id} className="bg-white">
                              <button
                                onClick={() => toggleQuestion(questionId)}
                                className="w-full px-6 py-4 text-left flex items-start justify-between hover:bg-gray-50 transition-colors duration-200 group"
                              >
                                <h3 className="text-base md:text-lg font-medium text-[#1E3A5F] pr-4 group-hover:text-[#2AA8B0] transition-colors">
                                  {faq.question}
                                </h3>
                                <svg
                                  className={`w-5 h-5 text-[#2AA8B0] transform transition-transform duration-200 flex-shrink-0 mt-1 ${
                                    isQuestionOpen ? 'rotate-180' : ''
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M19 9l-7 7-7-7" 
                                  />
                                </svg>
                              </button>

                              {isQuestionOpen && (
                                <div className="px-6 pb-6 pt-2 bg-gray-50 animate-fadeIn">
                                  <div 
                                    className="text-gray-700 leading-relaxed prose prose-sm max-w-none prose-p:mb-3 prose-ul:my-2 prose-li:my-1 prose-strong:text-gray-900"
                                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Call to Action */}
        <section className="bg-gradient-to-r from-[#2AA8B0] to-[#1E3A5F] text-white py-16 mx-4 rounded-lg mb-8">
          <div className="container mx-auto px-4 text-center">
            <Mail size={48} className="mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4 font-montserrat">
              ¿No encontraste lo que buscabas?
            </h2>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              Nuestro equipo está listo para ayudarte con cualquier consulta adicional. 
              No dudes en contactarnos, estamos aquí para hacer tu viaje realidad.
            </p>
            <button 
              onClick={() => window.location.href = '/contacto'}
              className="bg-white text-[#2AA8B0] font-bold px-8 py-3 rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Contáctanos
            </button>
          </div>
        </section>

        {/* Información de contacto */}
        <div className="container mx-auto px-4 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Oficina */}
            <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#2AA8B0] rounded-full mx-auto mb-4 flex items-center justify-center">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#1E3A5F] mb-3">Nuestra Oficina</h3>
              <p className="text-gray-600 text-sm mb-2">Yerbal 1136 PB</p>
              <p className="text-gray-600 text-sm mb-2">(C1406BWA)</p>
              <p className="text-gray-600 text-sm font-medium">CABA, Argentina</p>
            </div>

            {/* Horarios */}
            <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#2AA8B0] rounded-full mx-auto mb-4 flex items-center justify-center">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#1E3A5F] mb-3">Horarios de Atención</h3>
              <p className="text-gray-600 text-sm mb-1"><strong>Lunes a Viernes</strong></p>
              <p className="text-gray-600 text-sm mb-3">10:00 - 13:00 hs<br/>14:00 - 18:00 hs</p>
              <p className="text-gray-600 text-sm mb-1"><strong>Sábados</strong></p>
              <p className="text-gray-600 text-sm">10:00 - 13:00 hs</p>
            </div>

            {/* Contacto */}
            <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-[#2AA8B0] rounded-full mx-auto mb-4 flex items-center justify-center">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#1E3A5F] mb-3">Contacto Directo</h3>
              <a 
                href="tel:01144328675" 
                className="text-gray-600 text-sm block mb-2 hover:text-[#2AA8B0] font-medium"
              >
                📞 (011) 4432 8675
              </a>
              <a 
                href="mailto:consultas@nocheydiatursimo.com" 
                className="text-gray-600 text-sm block hover:text-[#2AA8B0] break-all"
              >
                ✉️ consultas@nocheydiatursimo.com
              </a>
            </div>
          </div>
        </div>

        <Footer />
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
}

export default FAQ;